import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShip, faBox, faPlus, faCalculator, faCalendarAlt, faFileAlt, faAnchor, faHandshake, faIdCard, faBuilding, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';

import systemDataService from '../../services/systemDataService';
import stockService from '../../services/stockService';
import { applyPickedResultToLine, pickedResultCurrency, pickedResultLabel } from '../../components/Invoicing/utils/pickedSearchResult';
import { fillPendingVatFigures, lineVatFigures, resolveVatPercentage, PENDING_FIGURE } from '../../components/Invoicing/utils/vatPercentage';
import InvoiceValidationModal, { clearFieldError } from '../../components/Invoicing/InvoiceValidationModal';
import InvoiceDatePicker from '../../components/ui/InvoiceDatePicker';
import SearchableSelect from '../../components/Invoicing/SearchableSelect';
import VatRateSelect from '../../components/Invoicing/VatRateSelect';
import InlineProductCreator from '../../components/Stock/InlineProductCreator';
import EditableUnitPriceCell from '../../components/Invoicing/EditableUnitPriceCell';
import VesselSearchAutocomplete from '../../components/VesselSearchAutocomplete';
import PortsDropdown from '../../components/PortsDropdown';
import { INCOTERMS_OPTIONS } from '../../utils/constants';
import { exceedsDecimals } from '../../utils/amountFormat';
import { PRICE_DECIMALS, QUANTITY_DECIMALS, stepFor, formatFigure, lineNet } from '../../utils/precision';
import './OutgoingInvoice.css';

/**
 * Screen 2: Products & Line Items
 * 
 * Sections:
 * - Product/Vessel Information (if from delivery)
 * - Line Items Table (dynamic add/remove)
 * - Totals Summary
 * - Gross Amount in Words
 */

const ProformaInvoiceScreen2 = ({ 
  invoiceData, 
  setInvoiceData,
  lineItems,
  setLineItems,
  vesselInfo,
  setVesselInfo,
  onPrevious,
  onNext,
  isReadOnly = false
}) => {
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const vatPercentageCache = useRef({});

  // Fuels invoices use the 'fuels_eng' template (auto-stamped by Sprint 4C
  // delivery_mapper for fuels deliveries). When true we relabel B/L -> BDN
  // on the vessel-info block. Solids invoices keep B/L unchanged.
  const isFuelsInvoice = invoiceData?.template_type === 'fuels_eng';
  const blLabelText = isFuelsInvoice ? 'BDN' : 'B/L';

  // Currency options
  const currencies = ['USD', 'EUR', 'GBP', 'ZAR', 'JPY', 'CNY', 'AUD', 'CAD', 'CHF', 'INR', 'CZK', 'SGD', 'AED', 'VND'];
  
  // Format number based on selected locale
  const formatNumberByLocale = (value, decimals = 2) => {
    if (!value || isNaN(value)) return decimals === 2 ? '0.00' : '0.0000';
    const num = parseFloat(value);
    const format = invoiceData.number_format || 'US';
    
    if (format === 'EU') {
      // European: 10.000,00
      const formatted = num.toFixed(decimals);
      const parts = formatted.split('.');
      const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return `${integerPart},${parts[1]}`;
    } else {
      // US: 10,000.00
      return num.toLocaleString('en-US', { 
        minimumFractionDigits: decimals, 
        maximumFractionDigits: decimals 
      });
    }
  };
  
  // Currency validation state
  const [showCurrencyErrorModal, setShowCurrencyErrorModal] = useState(false);
  const [currencyErrorMessage, setCurrencyErrorMessage] = useState('');
  
  // Product variant search state (per line item)
  const [variantSearchTerms, setVariantSearchTerms] = useState({});
  const [debouncedVariantSearches, setDebouncedVariantSearches] = useState({});
  const [filteredVariants, setFilteredVariants] = useState({});
  const [showVariantDropdown, setShowVariantDropdown] = useState({});
  const [variantSearchLoading, setVariantSearchLoading] = useState({});
  // Why the last search failed, per line. A failed search must never
  // masquerade as an empty catalogue (see stockService.productSearchErrorMessage).
  const [variantSearchError, setVariantSearchError] = useState({});
  const variantSearchRefs = useRef({});

  // Sprint D — Inline product creator state.
  const [productCreatorState, setProductCreatorState] = useState({
    isOpen: false,
    lineIndex: null,
    searchTerm: '',
  });
  const openProductCreator = (lineIndex, searchTerm) => {
    setProductCreatorState({ isOpen: true, lineIndex, searchTerm: searchTerm || '' });
    setShowVariantDropdown(prev => ({ ...prev, [lineIndex]: false }));
  };
  const closeProductCreator = () => {
    setProductCreatorState({ isOpen: false, lineIndex: null, searchTerm: '' });
  };
  const handleInlineProductCreated = (variant) => {
    const lineIndex = productCreatorState.lineIndex;
    if (lineIndex === null || !variant) {
      closeProductCreator();
      return;
    }
    handleSelectVariant(lineIndex, variant);
    closeProductCreator();
  };
  // Fired when InlineProductCreator's ServiceQuickForm succeeds.
  // Services have no pricing, so we fill the line's description + default GL
  // account; the operator enters qty / unit price / VAT on the line itself.
  const handleInlineServiceCreated = (service) => {
    const lineIndex = productCreatorState.lineIndex;
    if (lineIndex === null || !service) {
      closeProductCreator();
      return;
    }
    const description = service.description || service.service_type_display || '';
    const updated = [...lineItems];
    updated[lineIndex] = {
      ...updated[lineIndex],
      product: null,
      item_description: description,
      gl_account: service.gl_account || updated[lineIndex].gl_account || null,
    };
    setLineItems(updated);
    setVariantSearchTerms(prev => ({ ...prev, [lineIndex]: description }));
    closeProductCreator();
  };

  // Mirror the delivery "HOW" section: selecting a vessel from the API fills
  // both the vessel name and IMO (IMO has no separate lookup, same as delivery).
  const handleVesselSelect = (vessel) => {
    setVesselInfo(prev => ({
      ...prev,
      vessel_name: vessel?.ship_name || prev?.vessel_name || '',
      imo_number: vessel?.imo || '',
    }));
  };

  useEffect(() => {
    loadReferenceData();
    
    // Initialize with one line item if empty
    if (lineItems.length === 0) {
      addLineItem();
    }
    
    // Prevent scroll from changing number inputs
    const preventScroll = (e) => {
      if (document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };
    document.addEventListener('wheel', preventScroll, { passive: true });
    return () => document.removeEventListener('wheel', preventScroll);
  }, []);

  const entityId = invoiceData?.hms_entity;
  const glAccountParams = entityId ? { entity: entityId } : {};
  const vatRateParams = entityId ? { entity_id: entityId } : {};
  
  useEffect(() => {
    // Recalculate totals when line items change
    calculateTotals();
  }, [lineItems]);
  
  // Debounce variant search for each line item
  useEffect(() => {
    const timers = {};
    
    Object.keys(variantSearchTerms).forEach(lineIndex => {
      timers[lineIndex] = setTimeout(() => {
        setDebouncedVariantSearches(prev => ({
          ...prev,
          [lineIndex]: variantSearchTerms[lineIndex]
        }));
      }, 300);
    });
    
    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, [variantSearchTerms]);
  
  // Fetch variants when debounced search changes
  useEffect(() => {
    Object.keys(debouncedVariantSearches).forEach(lineIndex => {
      const searchTerm = debouncedVariantSearches[lineIndex];
      if (searchTerm && searchTerm.trim().length >= 2) {
        fetchVariantsForLineItem(lineIndex, searchTerm);
      } else {
        setFilteredVariants(prev => ({ ...prev, [lineIndex]: [] }));
      }
    });
  }, [debouncedVariantSearches]);
  
  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(variantSearchRefs.current).forEach(lineIndex => {
        const ref = variantSearchRefs.current[lineIndex];
        if (ref && !ref.contains(event.target)) {
          setShowVariantDropdown(prev => ({ ...prev, [lineIndex]: false }));
        }
      });
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadReferenceData = async () => {
    try {
      const [unitsData] = await Promise.all([
        systemDataService.getUnits()
      ]);
      
      // Map units to dropdown format, filter only active units
      const formattedUnits = unitsData
        .filter(unit => unit.is_active)
        .map(unit => ({
          value: unit.id,
          label: unit.name
        }));
      setUnits(formattedUnits);
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  };
  
  const fetchVariantsForLineItem = async (lineIndex, searchTerm) => {
    try {
      setVariantSearchLoading(prev => ({ ...prev, [lineIndex]: true }));
      
      // Proforma Invoices use sale_price (same as Outgoing)
      const variants = await stockService.searchVariantsForInvoice(searchTerm, 'PROFORMA', { includeServices: true });
      
      setFilteredVariants(prev => ({ ...prev, [lineIndex]: variants }));
      setVariantSearchError(prev => ({ ...prev, [lineIndex]: null }));
      setVariantSearchLoading(prev => ({ ...prev, [lineIndex]: false }));
    } catch (error) {
      console.error('Error fetching variants for line item:', error);
      setFilteredVariants(prev => ({ ...prev, [lineIndex]: [] }));
      setVariantSearchError(prev => ({
        ...prev,
        [lineIndex]: stockService.productSearchErrorMessage(error),
      }));
      setVariantSearchLoading(prev => ({ ...prev, [lineIndex]: false }));
    }
  };
  
  const autoResizeTextarea = (index) => {
    setTimeout(() => {
      const textarea = document.getElementById(`line_item_description_${index}`);
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      }
    }, 0);
  };

  const handleSelectVariant = (lineIndex, variant) => {
    console.log('Selected variant for line', lineIndex, ':', variant);
    
    // Get variant currency
    const variantCurrency = pickedResultCurrency(variant);
    
    // If not the first line item, validate currency matches first line item
    if (variantCurrency && lineIndex > 0 && lineItems.length > 0) {
      const firstItemCurrency = lineItems[0].currency;
      if (variantCurrency !== firstItemCurrency) {
        // Show error modal and prevent selection
        setCurrencyErrorMessage(`Only items with ${firstItemCurrency} currency can be added. The selected item has ${variantCurrency} currency. All line items must use the same currency as the first line item.`);
        setShowCurrencyErrorModal(true);
        // Close dropdown without selecting
        setShowVariantDropdown(prev => ({ ...prev, [lineIndex]: false }));
        return;
      }
    }
    
    // Update the line item with variant data
    const updated = [...lineItems];
    const cleanDescription = pickedResultLabel(variant);
    // A picked service fills description + GL account and never the product slot.
    updated[lineIndex] = applyPickedResultToLine(updated[lineIndex], variant, { priceField: 'sale_price', units });
    
    // Recalculate amounts for this line
    calculateLineItemAmounts(updated, lineIndex, 'unit_price');
    
    setLineItems(updated);
    
    // Update search term to show selected item (without pricing)
    setVariantSearchTerms(prev => ({ 
      ...prev, 
      [lineIndex]: cleanDescription 
    }));
    
    // Close dropdown
    setShowVariantDropdown(prev => ({ ...prev, [lineIndex]: false }));
    
    // Auto-resize textarea to fit content
    autoResizeTextarea(lineIndex);
  };
  
  const addLineItem = () => {
    const newLineItem = {
      line_number: lineItems.length + 1,
      item_description: '',
      product: null,
      quantity: 0,
      unit: units.length > 0 ? units[0].value : null,
      unit_price: 0,
      // Session-only UX flag — set to true once the user confirms a
      // manual override of the catalog price on a linked line. Drives
      // the "Price overridden" badge under the unit_price input.
      unit_price_overridden: false,
      net_amount: 0,
      currency: invoiceData.currency || 'USD',
      vat_rate: null,
      vat_amount: 0,
      gross_amount: 0,
      gl_account: null
    };
    
    setLineItems([...lineItems, newLineItem]);
  };
  
  const removeLineItem = (index) => {
    const updated = lineItems.filter((_, i) => i !== index);
    // Renumber
    updated.forEach((item, idx) => {
      item.line_number = idx + 1;
    });
    setLineItems(updated);
  };
  
  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    
    // Currency validation and synchronization
    if (field === 'currency') {
      if (index === 0) {
        // First line item: update it and sync all other line items to match
        updated[index] = { ...updated[index], [field]: value };
        // Update all other line items to match the first line item's currency
        for (let i = 1; i < updated.length; i++) {
          updated[i] = { ...updated[i], currency: value };
        }
      } else {
        // Not the first line item: validate against first line item's currency
        const firstItemCurrency = lineItems[0].currency;
        if (value !== firstItemCurrency) {
          // Show error modal and prevent the change
          setCurrencyErrorMessage(`You cannot change the currency. All line items must use ${firstItemCurrency} as selected in the first line item.`);
          setShowCurrencyErrorModal(true);
          return; // Don't update the line item
        }
        // Currency matches, allow the change
        updated[index] = { ...updated[index], [field]: value };
      }
    } else {
      // Non-currency field update
      updated[index] = { ...updated[index], [field]: value };

      // If the user just edited the price on a linked line, stamp the
      // override flag so the warning modal does not re-fire on every
      // subsequent keystroke and the "Price overridden" badge appears.
      if (field === 'unit_price' && updated[index].product) {
        updated[index].unit_price_overridden = true;
      }
    }
    
    // Recalculate amounts for this line if needed
    if (['quantity', 'unit_price', 'net_amount', 'vat_rate'].includes(field)) {
      calculateLineItemAmounts(updated, index, field);
    }
    
    setLineItems(updated);
  };
  
  const calculateLineItemAmounts = (items, index, changedField) => {
    const item = items[index];
    const quantity = parseFloat(item.quantity) || 0;
    const netAmount = parseFloat(item.net_amount) || 0;
    
    // Bidirectional calculation logic
    if (changedField === 'net_amount') {
      // If net amount was changed, calculate unit price from net amount and quantity
      if (quantity > 0) {
        item.unit_price = formatFigure(netAmount / quantity, 2, PRICE_DECIMALS);
      }
    } else if (changedField === 'quantity' || changedField === 'unit_price') {
      // If quantity or unit price was changed, calculate net amount.
      // Exact decimal product rounded to the cent, as the server does it.
      item.net_amount = lineNet(item.quantity, item.unit_price);
    }
    
    // VAT % through the one shared rule (vatPercentage.js): the picker's
    // cache by rate id first, a custom % next, no rate = 0. null = the rate
    // is not known yet: VAT and gross stay "—", never 0.00.
    Object.assign(item, lineVatFigures(item.net_amount, resolveVatPercentage(item, vatPercentageCache.current)));
  };

  const calculateTotals = () => {
    const netTotal = lineItems.reduce((sum, item) => sum + parseFloat(item.net_amount || 0), 0);
    const vatTotal = lineItems.reduce((sum, item) => sum + parseFloat(item.vat_amount || 0), 0);
    const grossTotal = lineItems.reduce((sum, item) => sum + parseFloat(item.gross_amount || 0), 0);
    
    setInvoiceData(prev => ({
      ...prev,
      net_amount: netTotal.toFixed(2),
      vat_amount: vatTotal.toFixed(2),
      gross_amount: grossTotal.toFixed(2)
    }));
  };
  
  // Convert number to words
  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    const [intPart, decPart] = num.toString().split('.');
    const intNum = parseInt(intPart);
    
    if (intNum === 0 && decPart) {
      const cents = parseInt(decPart.padEnd(2, '0').substring(0, 2));
      return cents === 0 ? 'Zero' : convertLessThanThousand(cents) + ' Cents';
    }
    
    let result = '';
    
    if (intNum >= 1000000) {
      result += convertLessThanThousand(Math.floor(intNum / 1000000)) + ' Million ';
    }
    if (intNum >= 1000) {
      result += convertLessThanThousand(Math.floor((intNum % 1000000) / 1000)) + ' Thousand ';
    }
    result += convertLessThanThousand(intNum % 1000);
    
    if (decPart) {
      const cents = parseInt(decPart.padEnd(2, '0').substring(0, 2));
      if (cents > 0) {
        result += ' and ' + convertLessThanThousand(cents) + ' Cents';
      }
    }
    
    return result.trim();
  };
  
  const handleNext = () => {
    onNext();
  };
  
  return (
    <>
      <div className="invoice-screen-content" style={{ overflow: 'visible' }}>
        {/* Vessel/Product Information - Always visible, auto-populates from delivery or manual input */}
        <div className="invoice-form-section">
        <h2 className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            <FontAwesomeIcon icon={faShip} /> Vessel & Shipment Information
          </span>
          <span style={{ fontSize: '0.85em', color: '#fff', fontWeight: 'normal' }}>
            {invoiceData.invoice_number ? `Invoice: ${invoiceData.invoice_number}` : '(number will be generated on save)'}
          </span>
        </h2>
        
        <div className="invoice-form-grid">
          {/* Left Column */}
          <div className="invoice-form-column">
            <div className="form-field">
              <label className="field-label"><FontAwesomeIcon icon={faShip} /> Vessel name</label>
              {isReadOnly ? (
                <input type="text" className="form-input" value={vesselInfo?.vessel_name || ''} readOnly />
              ) : (
                <VesselSearchAutocomplete
                  initialValue={vesselInfo?.vessel_name || ''}
                  onSelect={handleVesselSelect}
                  placeholder="Search vessel by Name, IMO, MMSI..."
                />
              )}
            </div>
            <div className="form-field">
              <InvoiceDatePicker
                value={vesselInfo?.delivery_date || ''}
                onChange={(e) => setVesselInfo(prev => ({ ...prev, delivery_date: e.target.value }))}
                name="delivery_date"
                id="delivery_date"
                label={<span><FontAwesomeIcon icon={faCalendarAlt} /> Delivery date</span>}
                placeholder="Select delivery date..."
                className="form-input"
                readOnly={isReadOnly}
              />
            </div>
            <div className="form-field">
              <label className="field-label"><FontAwesomeIcon icon={faFileAlt} /> {blLabelText} number</label>
              <input type="text" className="form-input" value={vesselInfo?.bl_number || ''} onChange={(e) => setVesselInfo(prev => ({ ...prev, bl_number: e.target.value }))} placeholder={`Enter ${blLabelText} number...`} readOnly={isReadOnly} />
            </div>
            <div className="form-field">
              <InvoiceDatePicker
                value={vesselInfo?.bl_date || ''}
                onChange={(e) => setVesselInfo(prev => ({ ...prev, bl_date: e.target.value }))}
                name="bl_date"
                id="bl_date"
                label={<span><FontAwesomeIcon icon={faCalendarAlt} /> {blLabelText} date</span>}
                placeholder={`Select ${blLabelText} date...`}
                className="form-input"
                readOnly={isReadOnly}
              />
            </div>
          </div>
          
          {/* Right Column */}
          <div className="invoice-form-column">
            <div className="form-field">
              <label className="field-label"><FontAwesomeIcon icon={faAnchor} /> Discharge port</label>
              {isReadOnly ? (
                <input type="text" className="form-input" value={vesselInfo?.discharge_port || ''} readOnly />
              ) : (
                <PortsDropdown
                  value={vesselInfo?.discharge_port || ''}
                  onChange={(value) => setVesselInfo(prev => ({ ...prev, discharge_port: value }))}
                  className="form-input"
                  placeholder="Select discharge port..."
                />
              )}
            </div>
            <div className="form-field">
              <label className="field-label"><FontAwesomeIcon icon={faAnchor} /> Loading port</label>
              {isReadOnly ? (
                <input type="text" className="form-input" value={vesselInfo?.loading_port || ''} readOnly />
              ) : (
                <PortsDropdown
                  value={vesselInfo?.loading_port || ''}
                  onChange={(value) => setVesselInfo(prev => ({ ...prev, loading_port: value }))}
                  className="form-input"
                  placeholder="Select loading port..."
                />
              )}
            </div>
            <div className="form-field">
              <label className="field-label"><FontAwesomeIcon icon={faHandshake} /> INCO terms</label>
              <select
                className="form-select"
                value={vesselInfo?.incoterms || ''}
                onChange={(e) => setVesselInfo(prev => ({ ...prev, incoterms: e.target.value }))}
                disabled={isReadOnly}
              >
                <option value="">Select INCO terms...</option>
                {INCOTERMS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label className="field-label"><FontAwesomeIcon icon={faIdCard} /> IMO</label>
              <input type="text" className="form-input" value={vesselInfo?.imo_number || ''} placeholder="Auto-filled from vessel selection" readOnly title="Auto-filled when you select a vessel above (same as the delivery)" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Line Items Table */}
      <div className="invoice-form-section" style={{ overflow: 'visible' }}>
        <h2 className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            <FontAwesomeIcon icon={faBox} /> Products
          </span>
          <span style={{ fontSize: '0.85em', color: '#fff', fontWeight: 'normal' }}>
            {invoiceData.invoice_number ? `Invoice: ${invoiceData.invoice_number}` : '(number will be generated on save)'}
          </span>
        </h2>
        
        <div className="line-items-table-container" style={{ overflow: 'visible' }}>
          <table className="line-items-table" style={{ overflow: 'visible' }}>
            <thead>
              <tr style={{ fontSize: '10px' }}>
                <th style={{ minWidth: '250px', fontSize: '10px' }}>Item Description / Product Search</th>
                <th style={{ minWidth: '90px', maxWidth: '100px', fontSize: '10px' }}>Quantity</th>
                <th style={{ minWidth: '80px', fontSize: '10px' }}>Unit</th>
                <th style={{ minWidth: '120px', fontSize: '10px' }}>GL Account</th>
                <th style={{ minWidth: '80px', fontSize: '10px' }}>Currency</th>
                <th style={{ minWidth: '30px', maxWidth: '100px', fontSize: '10px' }}>Unit Price</th>
                <th style={{ minWidth: '110px', maxWidth: '100px', fontSize: '10px' }}>Net Amount</th>
                <th style={{ minWidth: '120px', fontSize: '10px' }}>VAT Rate</th>
                <th style={{ minWidth: '110px', fontSize: '10px' }}>VAT Amount</th>
                <th style={{ minWidth: '120px', maxWidth: '150px', width: '120px', fontSize: '10px' }}>Gross Amount</th>
                <th style={{ minWidth: '35px', width: '35px', fontSize: '10px' }}></th>
              </tr>
            </thead>
            <tbody style={{ overflow: 'visible' }}>
              {lineItems.map((item, index) => (
                <tr key={index} style={{ fontSize: '10px', overflow: 'visible' }}>
                  <td className="line-item-cell" ref={el => variantSearchRefs.current[index] = el} style={{ position: 'relative', overflow: 'visible' }}>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <div style={{ position: 'relative' }}>
                        {!isReadOnly && (
                          <FontAwesomeIcon 
                            icon={faSearch} 
                            style={{ 
                              position: 'absolute', 
                              left: '8px', 
                              top: '12px', 
                              color: '#666',
                              pointerEvents: 'none',
                              fontSize: '10px',
                              zIndex: 1
                            }} 
                          />
                        )}
                        <textarea
                          id={`line_item_description_${index}`}
                          className="table-input"
                          style={{
                            fontSize: '10px',
                            paddingLeft: isReadOnly ? '8px' : '24px',
                            paddingRight: isReadOnly ? '8px' : '24px',
                            width: '100%',
                            minHeight: '32px',
                            resize: 'vertical',
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            overflow: 'auto',
                            ...(!item.product && !isReadOnly ? {
                              backgroundColor: '#eff6ff',
                              borderColor: '#93c5fd',
                            } : {}),
                          }}
                          rows={2}
                          value={variantSearchTerms[index] || item.item_description || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setVariantSearchTerms(prev => ({ ...prev, [index]: value }));
                            setShowVariantDropdown(prev => ({ ...prev, [index]: true }));
                            
                            // If user is typing, allow manual entry
                            if (!showVariantDropdown[index]) {
                              updateLineItem(index, 'item_description', value);
                            }
                            clearFieldError(`line_item_description_${index}`);
                            
                            // Auto-resize as user types
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }}
                          onFocus={() => setShowVariantDropdown(prev => ({ ...prev, [index]: true }))}
                          placeholder="Search products or enter manually..."
                          readOnly={isReadOnly}
                        />
                        {!isReadOnly && (variantSearchTerms[index] || item.item_description) && (
                          <FontAwesomeIcon 
                            icon={faTimes} 
                            onClick={(e) => {
                              e.stopPropagation();
                              setVariantSearchTerms(prev => ({ ...prev, [index]: '' }));
                              const updated = [...lineItems];
                              updated[index] = { ...updated[index], item_description: '', product: null };
                              setLineItems(updated);
                              setShowVariantDropdown(prev => ({ ...prev, [index]: false }));
                            }}
                            style={{ 
                              position: 'absolute', 
                              right: '8px', 
                              top: '12px', 
                              color: '#999',
                              cursor: 'pointer',
                              fontSize: '10px',
                              zIndex: 1
                            }}
                          />
                        )}
                      </div>
                      
                      {showVariantDropdown[index] && !isReadOnly && (
                        <div 
                          className="partner-dropdown"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            minWidth: '300px',
                            maxWidth: '500px',
                            maxHeight: '250px',
                            overflowY: 'auto',
                            backgroundColor: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            zIndex: 9999,
                            marginTop: '2px'
                          }}
                        >
                          {variantSearchLoading[index] && (
                            <div 
                              style={{ 
                                padding: '12px', 
                                textAlign: 'center',
                                color: '#666',
                                fontSize: '10px'
                              }}
                            >
                              Searching products...
                            </div>
                          )}
                          
                          {!variantSearchLoading[index] && filteredVariants[index] && filteredVariants[index].length > 0 && (
                            filteredVariants[index].map((variant) => (
                              <div
                                key={variant.id}
                                className="partner-dropdown-item"
                                onClick={() => handleSelectVariant(index, variant)}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #eee',
                                  fontSize: '10px',
                                  lineHeight: '1.4'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <div style={{ fontWeight: '500', color: '#333' }}>
                                  {variant.display_name}
                                </div>
                                {variant.quality && (
                                  <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>
                                    Quality: {variant.quality.substring(0, 50)}{variant.quality.length > 50 ? '...' : ''}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                          
                          {!variantSearchLoading[index] && (!filteredVariants[index] || filteredVariants[index].length === 0) && (
                            <div
                              style={{
                                padding: '12px',
                                textAlign: 'center',
                                color: '#666',
                                fontSize: '10px'
                              }}
                            >
                              <div style={{ marginBottom: '6px', color: variantSearchError[index] ? '#b91c1c' : undefined }}>
                                {variantSearchError[index]
                                  ? variantSearchError[index]
                                  : (debouncedVariantSearches[index] && debouncedVariantSearches[index].length >= 2
                                    ? 'No products found.'
                                    : 'No matching product yet — create one below.')}
                              </div>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openProductCreator(
                                      index,
                                      variantSearchTerms[index] || item.item_description || debouncedVariantSearches[index] || ''
                                    );
                                  }}
                                  style={{
                                    padding: '4px 10px',
                                    fontSize: '10px',
                                    backgroundColor: '#3b82f6',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                  }}
                                >
                                  + Create product / service
                                </button>
                                <span style={{ fontSize: '10px', color: '#999' }}>or enter manually above</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Sprint D follow-up — per-line product status chip.
                          Two mutually exclusive states surfaced under the
                          description input so the user can see at a glance
                          whether the line is linked or needs picking/creating.
                          Forced single-line via whiteSpace: nowrap; long copy
                          truncates with an ellipsis on narrow columns.
                          Hidden in read-only mode. */}
                      {item.product && !isReadOnly && (
                        <div
                          style={{
                            marginTop: '3px',
                            padding: '2px 6px',
                            backgroundColor: '#dcfce7',
                            border: '1px solid #86efac',
                            borderRadius: '3px',
                            fontSize: '9px',
                            color: '#166534',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title="This line is linked to a stock product variant"
                        >
                          Linked product
                        </div>
                      )}

                      {!item.product && !isReadOnly && (
                        <div
                          style={{
                            marginTop: '3px',
                            padding: '2px 6px',
                            backgroundColor: '#eff6ff',
                            border: '1px solid #93c5fd',
                            borderRadius: '3px',
                            fontSize: '9px',
                            color: '#1e40af',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title="Click into the description field to search the product catalog or create a new product"
                        >
                          <span style={{ fontWeight: 500 }}>No match</span>
                          <span style={{ color: '#475569' }}> — click above to pick or create</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="line-item-cell">
                    <input
                      id={`line_item_quantity_${index}`}
                      type="number"
                      step={stepFor(QUANTITY_DECIMALS)}
                      min={stepFor(QUANTITY_DECIMALS)}
                      className="table-input-number"
                      style={{ fontSize: '10px' }}
                      value={item.quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        // InvoiceLineItem.quantity stores QUANTITY_DECIMALS
                        // places (utils/precision.js). One more 400s the whole
                        // save, so refuse it here rather than at the API.
                        // See exceedsDecimals().
                        if (exceedsDecimals(value, QUANTITY_DECIMALS)) return;
                        updateLineItem(index, 'quantity', value);
                        clearFieldError(`line_item_quantity_${index}`);
                      }}
                      onKeyPress={(e) => {
                        if (!/[0-9.]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      placeholder="nr. + product"
                      readOnly={isReadOnly}
                    />
                  </td>
                  <td>
                    <select
                      className="table-select"
                      style={{ fontSize: '10px' }}
                      value={item.unit || ''}
                      onChange={(e) => updateLineItem(index, 'unit', e.target.value)}
                      disabled={isReadOnly}
                    >
                      <option value="">Select...</option>
                      {units.map(unit => (
                        <option key={unit.value} value={unit.value}>
                          {unit.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ overflow: 'visible' }}>
                    <SearchableSelect
                      fetchUrl="/api/invoicing/gl-accounts/"
                      params={glAccountParams}
                      displayFn={(gl) => `${gl.account_number} - ${gl.account_name}`}
                      value={item.gl_account || ''}
                      onChange={(id) => updateLineItem(index, 'gl_account', id)}
                      placeholder="Search GL..."
                      disabled={isReadOnly}
                      variant="table"
                      listOnOpen
                      pageSize={50}
                    />
                  </td>
                  <td>
                    <select
                      className="table-select"
                      style={{ fontSize: '10px' }}
                      value={item.currency}
                      onChange={(e) => updateLineItem(index, 'currency', e.target.value)}
                      disabled={isReadOnly}
                    >
                      {currencies.map(currency => (
                        <option key={currency} value={currency}>
                          {currency}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="line-item-cell">
                    <EditableUnitPriceCell
                      inputId={`line_item_unit_price_${index}`}
                      value={item.unit_price}
                      hasLinkedProduct={!!item.product}
                      isOverridden={!!item.unit_price_overridden}
                      disabled={isReadOnly}
                      productLabel={item.item_description}
                      currency={item.currency}
                      onConfirmedChange={(newValue) =>
                        updateLineItem(index, 'unit_price', newValue)
                      }
                    />
                  </td>
                  <td className="line-item-cell">
                    <input
                      id={`line_item_net_amount_${index}`}
                      type="text"
                      className="table-input-calculated"
                      style={{ fontSize: '10px' }}
                      value={formatNumberByLocale(item.net_amount, 2)}
                      readOnly
                    />
                  </td>
                  <td className="line-item-cell" style={{ overflow: 'visible' }}>
                    <VatRateSelect
                      params={vatRateParams}
                      value={item.vat_rate || ''}
                      onChange={(id, selectedItem) => {
                        if (selectedItem?.rate_percentage !== undefined) {
                          vatPercentageCache.current[id] = parseFloat(selectedItem.rate_percentage);
                        }
                        updateLineItem(index, 'vat_rate', id);
                        clearFieldError(`line_item_vat_rate_${index}`);
                      }}
                      onResolve={(resolvedItem) => {
                        if (resolvedItem?.id && resolvedItem?.rate_percentage !== undefined) {
                          vatPercentageCache.current[resolvedItem.id] = parseFloat(resolvedItem.rate_percentage);
                          // Lines left at "—" while this rate was unknown.
                          setLineItems(prev => fillPendingVatFigures(prev, vatPercentageCache.current));
                        }
                      }}
                      disabled={isReadOnly}
                      id={`line_item_vat_rate_${index}`}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="table-input-calculated"
                      style={{ fontSize: '10px' }}
                      value={item.vat_amount === null ? PENDING_FIGURE : formatNumberByLocale(item.vat_amount, 2)}
                      readOnly
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="table-input-calculated"
                      style={{ fontSize: '10px' }}
                      value={item.gross_amount === null ? PENDING_FIGURE : formatNumberByLocale(item.gross_amount, 2)}
                      readOnly
                    />
                  </td>
                  <td>
                    {!isReadOnly && (
                      <button
                        type="button"
                        className="btn-icon-danger"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                        style={{ fontSize: '20px', color: '#999', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {!isReadOnly && (
            <button type="button" className="btn-add-line" onClick={addLineItem}>
              <FontAwesomeIcon icon={faPlus} /> Add Item
            </button>
          )}
        </div>
        
        {/* Number Format Selection - positioned above totals */}
        <div className="number-format-selector" style={{ marginTop: '12px', marginBottom: '8px', marginRight: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <div>
            <label className="form-label" style={{ marginBottom: '4px', display: 'block', fontSize: '0.75rem', fontWeight: '500', color: '#374151' }}>
              Number Format for PDF
            </label>
            <div style={{ 
              display: 'inline-flex',
              flexDirection: 'row', 
              gap: '0.5rem',
              padding: '0.35rem 0.5rem',
              background: '#f8f9ff',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              width: 'fit-content'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.15rem 0' }}>
                <input
                  type="checkbox"
                  id="number-format-us-proforma"
                  className="due-date-checkbox"
                  checked={invoiceData.number_format === 'US' || !invoiceData.number_format}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setInvoiceData({ ...invoiceData, number_format: 'US' });
                    }
                  }}
                  disabled={isReadOnly}
                />
                <label htmlFor="number-format-us-proforma" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.75rem',
                  color: '#374151',
                  cursor: 'pointer',
                  userSelect: 'none',
                  margin: 0
                }}>
                  US Format (10,000.00)
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.15rem 0' }}>
                <input
                  type="checkbox"
                  id="number-format-eu-proforma"
                  className="due-date-checkbox"
                  checked={invoiceData.number_format === 'EU'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setInvoiceData({ ...invoiceData, number_format: 'EU' });
                    }
                  }}
                  disabled={isReadOnly}
                />
                <label htmlFor="number-format-eu-proforma" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.75rem',
                  color: '#374151',
                  cursor: 'pointer',
                  userSelect: 'none',
                  margin: 0
                }}>
                  European Format (10.000,00)
                </label>
              </div>
            </div>
          </div>
        </div>
        
        {/* Totals Summary */}
        <div className="invoice-totals-summary">
          <div className="left-column-totals">
            <div className="amount-in-words">
              <label>Gross amount in words:</label>
              <div className="words-display">
                {invoiceData.gross_amount && parseFloat(invoiceData.gross_amount) > 0
                  ? numberToWords(parseFloat(invoiceData.gross_amount)) + ` ${lineItems.length > 0 && lineItems[0].currency ? lineItems[0].currency : (invoiceData.currency || 'USD')}`
                  : 'Zero'}
              </div>
            </div>
          </div>
          
          <div className="totals-box">
            <div className="total-row">
              <span className="total-label">Net amount:</span>
              <span className="total-value">{formatNumberByLocale(invoiceData.net_amount, 2)}</span>
            </div>
            <div className="total-row">
              <span className="total-label">VAT amount:</span>
              <span className="total-value">{formatNumberByLocale(invoiceData.vat_amount, 2)}</span>
            </div>
            <div className="total-row">
              <span className="total-label">Gross amount:</span>
              <span className="total-value">{formatNumberByLocale(invoiceData.gross_amount, 2)}</span>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <div className="screen-navigation">
          <button className="btn-secondary" type="button" onClick={onPrevious}>
            <FontAwesomeIcon icon={faBuilding} /> Previous: Header & Partner
          </button>
          <button className="btn-primary" type="button" onClick={handleNext}>
            Next: Bank & Notes <FontAwesomeIcon icon={faCalculator} />
          </button>
        </div>
      </div>
    </div>
    
    {/* Currency Error Modal */}
    <InvoiceValidationModal
      isOpen={showCurrencyErrorModal}
      onClose={() => setShowCurrencyErrorModal(false)}
      title="Currency Mismatch"
      errors={[currencyErrorMessage]}
      fieldIds={[]}
    />

    {/* Sprint D — Inline product creator. Proforma is an AR surface ⇒ sale_price. */}
    {productCreatorState.isOpen && (() => {
      const triggeringLine = lineItems[productCreatorState.lineIndex] || {};
      return (
        <InlineProductCreator
          isOpen={true}
          onClose={closeProductCreator}
          onProductCreated={handleInlineProductCreated}
          initialSearchTerm={productCreatorState.searchTerm}
          initialPriceField="sale_price"
          initialUnitPrice={triggeringLine.unit_price || ''}
          initialCurrency={triggeringLine.currency || invoiceData.currency || 'USD'}
          initialQuantity={triggeringLine.quantity || ''}
          allowService
          onServiceCreated={handleInlineServiceCreated}
          initialServiceDescription={productCreatorState.searchTerm}
        />
      );
    })()}
    </>
  );
};

export default ProformaInvoiceScreen2;

